import { back, navigate, type Screen } from '@/app'
import {
  type ContentCard,
  type ContentDetail,
  type Episode,
  type UpcomingItem,
  getUpcomingItem,
  lookupContent,
} from '@/lib/catalog'
import { trailerAllowed } from '@/lib/ageGate'
import { resumeEpisode } from '@/lib/continue'
import { formatPremiere } from '@/lib/dates'
import { el, setText } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { IMAGE_WIDTH, mediaUrl, queueImage } from '@/lib/images'
import { readSession } from '@/lib/session'
import { cardFromDetail, isInWatchlist, toggleWatchlist } from '@/lib/watchlist'
import {
  openWithAgeGate,
  webButton,
  webCard,
  webDescription,
  webEmpty,
  webPage,
  webRow,
  webScreen,
} from '@/browser/ui'

/**
 * «Sonra İzlə» düyməsinin etiketi — cihaz film ekranındakı eyni funksiya.
 * ⚠ Açarlar lüğətdə hazır idi (`lib/i18n.ts`): simvol açarın İÇİNDƏDİR.
 */
function watchlistLabel(contentId: string): string {
  return isInWatchlist(contentId) ? T('✓  Siyahımdadır') : T('+  Sonra İzlə')
}

/**
 * Film/serial səhifəsi — brauzer variantı.
 *
 * ⚠ MODAL DEYİL, SƏHİFƏDİR. Cihaz app-ında detal ÜST QAT modaldır
 *   (`Docs/KINOBOX_DETAIL_MODAL.md`) — orada məqsəd arxadakı sıranın sürüşmə
 *   mövqeyini qorumaqdır, çünki ekranı yenidən qurmaq köhnə TV-də saniyələr
 *   çəkir. Brauzerdə isə geri qayıdış BRAUZERİN öz işidir və sıra doğma
 *   `scrollLeft` ilə onsuz da yerində qalır; üstəlik modal daxilində doğma
 *   sürüşmə TV brauzerində «tələ» yaradırdı (kənar qat sürüşür, iç qat yox).
 */
export function createWebDetailScreen(slug: string): Screen {
  const page = webPage()
  const loading = webEmpty(T('Yüklənir…'))
  page.appendChild(loading)

  let alive = true
  void lookupContent(slug).then((lookup) => {
    const detail = lookup.detail
    if (!alive || !page.parentNode) return
    if (loading.parentNode) loading.parentNode.removeChild(loading)
    if (!detail) {
      /*
        ⚠⚠ «TEZLİKLƏ» KONTENTİ BURADA ÖLÜ SƏHİFƏ VERİRDİ (MYTasks 709).
           `GET /api/catalog/content/{slug}` YALNIZ dərc olunmuşu (status=3)
           qaytarır — plana salınmış film (status=2) 404 alırdı və istifadəçi
           «Film açıla bilmədi» görürdü. Halbuki həmin filmin bütün məlumatı
           `…/upcoming/{slug}` ucundadır (təsvir · treyler · janr/əhval · HEYƏT ·
           premyera). İndi səhifə həmin cavabla qurulur.
        ⚠ SIRA BELƏDİR (əvvəl detal, sonra upcoming): dərc olunmuş filmlərin
          böyük əksəriyyəti birinci sorğuda cavab verir — ikinci gediş yalnız
          404 halında olur.
      */
      void getUpcomingItem(slug).then((item) => {
        if (!alive || !page.parentNode) return
        if (!item) {
          /*
            ⚠ 404 — «TAPILMADI», «YENİDƏN YOXLAYIN» YOX (BR 9.1, audit 2026-10-02).
              Hər iki uc 404 verəndə film yoxdur: uşaq profilində bağlıdır (16+),
              admin söndürüb və ya silinib. Əvvəl bu hal da «Bir azdan yenidən
              yoxlayın» deyirdi — yenidən yoxlamaq heç nəyi dəyişməz. Şəbəkə
              xətası/taymautda köhnə mesaj qalır.
          */
          page.appendChild(
            webEmpty(
              lookup.notFound
                ? T('Məlumat tapılmadı.')
                : T('Film açıla bilmədi. Bir azdan yenidən yoxlayın.'),
            ),
          )
          page.appendChild(centered(webButton(T('Geri'), () => back(), true)))
          return
        }
        gate(page, item, () => buildUpcoming(page, item))
      })
      return
    }
    gate(page, detail, () => build(page, detail))
  })

  // 2026-10-23: «Geri» üçün saxlanılır — izah `browser/ui.ts` → `webScreen` (keep-alive).
  return webScreen(page, () => {
    alive = false
  }, {})
}

/**
 * YAŞ QAPISI — `#/film/<slug>` bərpası ilə EYNİ (`openWithAgeGate`).
 *
 * ⚠⚠ 2026-10-25, kod oxunuşu: `#/detail/<slug>` ünvanı (səhifə yeniləmə, ünvan çubuğu,
 *    brauzerin «İrəli»si — `browser/history.ts` → `routeFromHash`) bu səhifəni QAPISIZ qururdu;
 *    qapı yalnız kartdan gələn yolda idi (`shell.ts`, oxşarlar sırası). ƏVVƏL yaşı çatmayan
 *    istifadəçi 18+ filmin səhifəsini, «▷ Treyler» düyməsini və «İzlə»ni görürdü. İndi səhifə
 *    qapı açılana qədər yalnız izah + «Geri» göstərir; kartdan gələn yol qapını artıq keçib,
 *    təkrar yoxlama pəncərəsiz dərhal açılır.
 */
function gate(page: HTMLElement, card: ContentCard, build: () => void): void {
  const waiting = webEmpty(T('Yaş məhdudiyyəti'))
  const back = centered(webButton(T('Geri'), () => goBack(), true))
  page.appendChild(waiting)
  page.appendChild(back)
  openWithAgeGate(card, () => {
    if (!page.parentNode) return
    if (waiting.parentNode) waiting.parentNode.removeChild(waiting)
    if (back.parentNode) back.parentNode.removeChild(back)
    build()
  })
}

function goBack(): void {
  back()
}

/**
 * «TEZLİKLƏ» SƏHİFƏSİ — plana salınmış film (MYTasks 709).
 *
 * ⚠ NƏ YOXDUR: «İzlə», reytinq, rəylər, bölümlər, oxşarlar — cihaz app-ındakı
 *   yüngül modalın eyni müqaviləsi (523/528). Səbəb məzmun deyil, HÜQUQdur:
 *   kontent dərc olunmayıb, bilet sorğusu server tərəfdə onsuz da rədd edilir.
 * ⚠ NƏ VAR: kadr · ad · meta · PREMYERA · janr/əhval · təsvir · HEYƏT · treyler.
 *   Treyler düyməsi oynadıcı ekranını açır — brauzerdə tam ekran BRAUZERİNdir
 *   (`Fullscreen API`, `screens/player.ts` «kino rejimi»), ayrıca düymə lazım
 *   deyil və modal da yoxdur (faylın başındakı «modal deyil, səhifədir» qərarı).
 */
function buildUpcoming(page: HTMLElement, item: UpcomingItem): void {
  const banner = el('div', 'web-detail__banner')
  const img = document.createElement('img')
  img.className = 'web-hero__img'
  img.alt = ''
  const source = item.backdropUrl || item.posterUrl
  if (source) queueImage(img, mediaUrl(source, IMAGE_WIDTH.backdrop))
  banner.appendChild(img)
  banner.appendChild(el('div', 'web-detail__scrim'))
  page.appendChild(banner)

  const body = el('div', 'web-detail__body')
  body.style.padding = '0 var(--web-pad)'
  page.appendChild(body)

  body.appendChild(el('h1', 'web-detail__title', item.title))

  // ⚠ REYTİNQ YOXDUR — film çıxmayıb, qiymətləndirmə də yoxdur (523 müqaviləsi).
  const meta: string[] = []
  if (item.year) meta.push(String(item.year))
  if (item.ageLimit) meta.push(item.ageLimit + '+')
  if (item.durationMinutes) meta.push(item.durationMinutes + T(' dəq'))
  const tags = (item.genreNames || []).concat(item.moodNames || [])
  if (tags.length) meta.push(tags.join(', '))
  if (meta.length) body.appendChild(el('p', 'web-detail__meta', meta.join(' · ')))

  const premiere = formatPremiere(item)
  if (premiere) {
    const line = el('p', 'web-detail__meta', T('Premyera') + ': ' + premiere)
    line.style.color = 'var(--warning)'
    body.appendChild(line)
  }

  if (item.description) body.appendChild(webDescription(item.description))

  const actions = el('div', 'web-detail__actions')
  // 2026-10-25: tam ekran treyleri `trailerAllowed`-dən keçir (girişsiz 18+ · profil həddi).
  if (item.trailerUrl && trailerAllowed(item.ageLimit)) {
    actions.appendChild(
      webButton(T('▷  Treyler'), () =>
        navigate({
          name: 'player',
          slug: item.slug,
          title: item.title,
          trailerAsset: item.trailerUrl,
          ageLimit: item.ageLimit,
        }),
      ),
    )
  }
  actions.appendChild(webButton(T('Geri'), () => back(), true))
  body.appendChild(actions)

  // Heyət — rejissor AYRI sətirdə (sahib: «heyət rejissor və s. da olsun»).
  // ⚠ Adlar MƏTNDİR: dərc olunmamış filmdən şəxs səhifəsinə keçid verilmir.
  const directors = item.directorNames || []
  if (directors.length) {
    const line = el('p', 'web-detail__cast')
    line.appendChild(el('b', '', T('Rejissor')))
    line.appendChild(document.createTextNode(directors.join(', ')))
    body.appendChild(line)
  }
  const cast = item.castNames || []
  if (cast.length) {
    const line = el('p', 'web-detail__cast')
    line.appendChild(el('b', '', T('Heyət')))
    line.appendChild(document.createTextNode(cast.slice(0, MAX_CAST_NAMES).join(', ')))
    body.appendChild(line)
  }
}

/** «Heyət» sətrində göstərilən ad sayı — dərc olunmuş filmdəki 12 ilə eyni qayda. */
const MAX_CAST_NAMES = 12

function centered(node: HTMLElement): HTMLElement {
  const wrap = el('div')
  wrap.style.textAlign = 'center'
  wrap.appendChild(node)
  return wrap
}

function build(page: HTMLElement, detail: ContentDetail): void {
  // ── Fon şəkli ────────────────────────────────────────────────────────────
  const banner = el('div', 'web-detail__banner')
  const img = document.createElement('img')
  img.className = 'web-hero__img'
  img.alt = ''
  const source = detail.backdropUrl || detail.posterUrl
  if (source) queueImage(img, mediaUrl(source, IMAGE_WIDTH.backdrop))
  banner.appendChild(img)
  banner.appendChild(el('div', 'web-detail__scrim'))
  page.appendChild(banner)

  const body = el('div', 'web-detail__body')
  body.style.padding = '0 var(--web-pad)'
  page.appendChild(body)

  body.appendChild(el('h1', 'web-detail__title', detail.title))

  const meta: string[] = []
  if (detail.year) meta.push(String(detail.year))
  if (detail.ageLimit) meta.push(detail.ageLimit + '+')
  if (detail.durationMinutes) meta.push(detail.durationMinutes + T(' dəq'))
  if (detail.rating) meta.push('★ ' + detail.rating.toFixed(1))
  if (detail.genres.length) meta.push(detail.genres.join(', '))
  if (meta.length) body.appendChild(el('p', 'web-detail__meta', meta.join(' · ')))

  if (detail.description) body.appendChild(webDescription(detail.description))

  // ── Aksiyalar ────────────────────────────────────────────────────────────
  const actions = el('div', 'web-detail__actions')
  // 2026-10-25: serialda ən son yarımçıq bölüm — izah `lib/continue.ts` → `resumeEpisode`.
  //   Seçim basış anında edilir: səhifə «Geri» ilə saxlanılır (keep-alive), bölüm isə dəyişə bilər.

  if (detail.isComingSoon === true) {
    // Oynatma bağlıdır (MYTasks 499) — düymə var, amma söndürülmüş vəziyyətdə.
    const soon = webButton(T('Tezliklə'), () => {}, true)
    soon.disabled = true
    actions.appendChild(soon)
  } else {
    actions.appendChild(
      webButton(T('▶  İzlə'), () => {
        // ⚠ Girişsiz «İzlə» → QR; treyler AÇILMIR (cihaz ekranı ilə eyni qayda).
        if (!readSession()) {
          navigate({ name: 'qr' })
          return
        }
        const startEpisode = detail.episodes.length ? resumeEpisode(detail.id, detail.episodes) : null
        if (startEpisode) {
          playEpisode(detail, startEpisode)
          return
        }
        navigate({
          name: 'player',
          slug: detail.slug,
          title: detail.title,
          contentId: detail.id,
          trailerAsset: detail.trailerUrl,
          ageLimit: detail.ageLimit,
          /*
            ⚠⚠ SONLUQ QATININ MƏLUMATI DA ÖTÜRÜLÜR (2026-09-06). NƏ ÖLÇÜLDÜ:
               tv.kinobox.az-da «Böyük dayaq» sona 28,5 saniyə qalmış sonluq qatını
               açdı, amma qatın İÇİ yalnız ulduzlar idi — `player__outro-grid` və
               `player__outro-card` DOM-da ÜMUMİYYƏTLƏ yox idi.
               ƏVVƏL NƏ OLURDU: `openOutro()` xanaları `options.similar`-dan qurur;
               brauzer variantı isə `similar` və `outroSeconds` göndərmirdi (cihaz
               variantı — `screens/detail.ts:180` — göndərirdi). Yəni dörd xana, onların
               səssiz treyleri və xanadan birbaşa oynatma veb TV-də HEÇ VAXT görünməyib.
               NİYƏ MƏHZ BU HƏLL: qat və xanalar artıq ortaq komponentdədir
               (`ui/watchOutro.ts`) — çatışmayan yalnız marşruta qoyulan iki sahə idi.
          */
          similar: detail.similar,
          outroSeconds: detail.outroSeconds,
        })
      }),
    )
  }

  // 2026-10-25: tam ekran treyleri `trailerAllowed`-dən keçir (girişsiz 18+ · profil həddi).
  if (detail.trailerUrl && trailerAllowed(detail.ageLimit)) {
    actions.appendChild(
      webButton(
        T('▷  Treyler'),
        () =>
          navigate({
            name: 'player',
            slug: detail.slug,
            title: detail.title,
            trailerAsset: detail.trailerUrl,
            ageLimit: detail.ageLimit,
          }),
        true,
      ),
    )
  }

  /*
    ⚠⚠ «SONRA İZLƏ» — 2026-08-30-da GERİ QAYTARILDI (sahib tələbi: «bütün tv
       projelərdə sonra izlə modulunu əlavə et»). 2026-08-04-də düymə bütün TV
       səthlərindən çıxarılmışdı; həmin qərar artıq qüvvədə deyil.
    ⚠ «TEZLİKLƏ» SƏHİFƏSİNDƏ (`buildUpcoming`) YOXDUR — dörd səthin hamısında
      eyni qərar: dərc olunmamış kontentdə modal/səhifə yalnız treyler + məlumat
      göstərir (523 müqaviləsi), aksiya düymələri orada ümumiyyətlə yığışdırılıb.
    ⚠ GİRİŞSİZ İSTİFADƏÇİ QR EKRANINA GEDİR — yuxarıdakı «İzlə» ilə EYNİ yol
      (MYTasks 214); səssiz uğursuzluq «düymə işləmir» kimi görünərdi.
    ⚠ SESSİYA VAR, PROFİL SEÇİLMƏYİBSƏ «Kim baxır?»-a (2026-08-30) — cihaz film
      ekranındakı eyni qərar; izahı `lib/watchlist.ts`-dədir.
  */
  const later = webButton(
    watchlistLabel(detail.id),
    () => {
      toggleWatchlist(
        detail.id,
        cardFromDetail(detail),
        () => setText(later, watchlistLabel(detail.id)),
        () => navigate({ name: 'qr' }),
        () => navigate({ name: 'profiles', replaceOnPick: false }),
      )
    },
    true,
  )
  actions.appendChild(later)

  actions.appendChild(webButton(T('Geri'), () => back(), true))
  body.appendChild(actions)

  // ── Heyət ────────────────────────────────────────────────────────────────
  if (detail.cast.length) {
    const names: string[] = []
    for (let i = 0; i < detail.cast.length && i < 12; i++) {
      const member = detail.cast[i]
      if (member) names.push(member.fullName)
    }
    const line = el('p', 'web-detail__cast')
    const label = el('b', '', T('Heyət'))
    line.appendChild(label)
    line.appendChild(document.createTextNode(names.join(', ')))
    body.appendChild(line)
  }

  // ── Bölümlər ─────────────────────────────────────────────────────────────
  if (detail.episodes.length) {
    const head = el('h2', 'web-row__title', T('Bölümlər'))
    head.style.margin = '28px 0 12px 0'
    body.appendChild(head)

    for (let i = 0; i < detail.episodes.length; i++) {
      const episode = detail.episodes[i]
      if (!episode) continue
      body.appendChild(episodeRow(detail, episode))
    }
  }

  // ── Oxşar ────────────────────────────────────────────────────────────────
  if (detail.similar.length) {
    body.appendChild(
      webRow(
        T('Oxşar'),
        detail.similar.map((card) =>
          webCard(card, (picked) =>
            // ⚠ `replace` — oxşardan oxşara keçəndə yığın şişməsin (cihaz ekranındakı eyni qayda).
            openWithAgeGate(picked, () => navigate({ name: 'detail', slug: picked.slug }, true)),
          ),
        ),
      ),
    )
  }
}

/**
 * Bölüm sətri — kadr (nömrə nişanı ilə) · ad · təsvir · müddət.
 *
 * ⚠⚠ NÖMRƏ KADRIN SOL-AŞAĞI KÜNCÜNDƏ NİŞANDIR (sahib tələbi 2026-08-25).
 *   ÖLÇÜLDÜ (2026-09-06, tv.kinobox.az, «Kuklaxana bütün bölümlər»): brauzer
 *   variantında bölümlər KADRSIZ mətn sətri idi — nömrə sətrin əvvəlində boz
 *   rəqəm (`.web-ep__no`), yəni nişan da, kadr da yox idi. Tələb üç TV səthində
 *   (Tizen/webOS cihaz variantı · Android TV · Apple TV) tətbiq olunmuşdu,
 *   veb TV isə köhnə siyahıda qalmışdı — dörd səth eyni serialı fərqli göstərirdi.
 *   NİYƏ MƏHZ BU HƏLL: bölümün öz kadrı onsuz da API-dədir (`thumbnailUrl`),
 *   yəni əlavə sorğu yoxdur; kadr olmayanda nişan boş çərçivənin üstündə qalır,
 *   sətir isə yenə oxunur.
 */
export function episodeRow(detail: ContentDetail, episode: Episode): HTMLElement {
  const node = document.createElement('button')
  node.type = 'button'
  node.className = 'web-ep web-focusable'

  const frame = el('span', 'web-ep__frame')
  if (episode.thumbnailUrl) {
    const thumb = document.createElement('img')
    thumb.className = 'web-ep__thumb'
    thumb.alt = ''
    frame.appendChild(thumb)
    queueImage(thumb, mediaUrl(episode.thumbnailUrl, IMAGE_WIDTH.card))
  }
  frame.appendChild(el('span', 'web-ep__no', String(episode.number)))
  node.appendChild(frame)

  const main = el('div', 'web-ep__main')
  main.appendChild(el('div', 'web-ep__title', episode.title || T('Bölüm ') + episode.number))
  if (episode.description) main.appendChild(el('div', 'web-ep__desc', episode.description))
  node.appendChild(main)

  // ⚠ SAĞ SÜTUN HƏMİŞƏ QURULUR: videosuz bölümdə səbəb məhz burada yazılır
  //   («hazır deyil»), yəni müddəti olmayan bölümdə element yoxdursa mesajı
  //   yazacaq yer də qalmazdı və düymə səssizcə işləməyən görünərdi.
  const right = el('span', 'web-ep__dur', episode.durationMinutes ? episode.durationMinutes + T(' dəq') : '')
  node.appendChild(right)

  if (!episode.hasVideo) {
    node.disabled = true
    setText(right, T('hazır deyil'))
  }

  node.onclick = () => {
    if (!readSession()) {
      navigate({ name: 'qr' })
      return
    }
    playEpisode(detail, episode)
  }
  return node
}

/** Bölümü açır — siyahı da ötürülür ki, player bitəndə növbətisini tapsın. */
function playEpisode(detail: ContentDetail, episode: Episode): void {
  navigate({
    name: 'player',
    slug: detail.slug,
    title: detail.title + ' · ' + (episode.title || T('Bölüm ') + episode.number),
    contentId: detail.id,
    episodeId: episode.id,
    episodes: detail.episodes,
    ageLimit: detail.ageLimit,
  })
}
